import { EventDeviceRow, EventOperatorRow, EventRaceSummaryRow, EventUserOption } from '../types';
import type { DevicesGroupedByRace } from '../types/EventsOverviewTypes';
import {
  showPrefixedToastError,
  showToastError,
  showToastErrorMessage,
} from '../../../helpers/toastError';

export type { RaceConfigurationRequirements } from '../../../helpers/deviceValidation';
export type { DevicesGroupedByRace };

export {
  BTN_PRIMARY,
  BTN_SECONDARY,
  BTN_GHOST,
  BTN_ICON,
} from '../../shared/ui/uiTokens';

export const RACE_STATUS_FLOW = ['DRAFT', 'PACKAGING', 'DISTRIBUTION', 'RACKING', 'ACTIVE', 'REPORTING', 'COMPLETE'] as const;

/** Canonical race status from API (DRAFT, PACKAGING, DISTRIBUTION, RACKING, ACTIVE, COMPLETE, DELETED). */
export function normalizeRaceStatus(status?: string | null): string {
  return String(status ?? '').trim().toUpperCase();
}

/** Display label for status pills (canonical form after normalization). */
export function formatStatusLabel(status?: string | null): string {
  const normalized = normalizeRaceStatus(status);
  return normalized || 'UNKNOWN';
}

export function isDraftRaceStatus(status?: string | null): boolean {
  return normalizeRaceStatus(status) === 'DRAFT';
}

/** True when the event has at least one race past Draft (devices / validation apply). */
export function eventHasAssignableDeviceRaces(
  races: Array<{ status?: string | null }> | null | undefined,
): boolean {
  if (!races?.length) return false;
  return races.some((r) => {
    const s = normalizeRaceStatus(r.status);
    return s !== 'DRAFT' && s !== 'DELETED';
  });
}

/** True when the race row has a saved athletes.csv on the server (Draft → Device Packaging gate). */
export function raceHasUploadedAthletesRoster(race: {
  latestAthletesFileId?: string | null;
} | null | undefined): boolean {
  return !!String(race?.latestAthletesFileId ?? '').trim();
}

export function dispatchToast(type: 'toast-info' | 'toast-error', message: string): void {
  if (type === 'toast-error') {
    showToastErrorMessage(message);
    return;
  }
  window.dispatchEvent(new CustomEvent(type, { detail: message }));
}

export function dispatchToastError(err: unknown, fallback?: string): void;
export function dispatchToastError(prefix: string, err: unknown, fallback?: string): void;
export function dispatchToastError(a: unknown, b?: unknown, c?: string): void {
  if (typeof a === 'string' && b !== undefined) {
    showPrefixedToastError(a, b, typeof c === 'string' ? c : 'Unknown error');
    return;
  }
  showToastError(a, typeof b === 'string' ? b : 'Unknown error');
}

function pushUniqueMessage(parts: string[], seen: Set<string>, value: unknown): void {
  if (typeof value !== 'string') return;
  const message = value.trim();
  if (!message) return;
  const key = message.toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  parts.push(message);
}

function isEventLockedAssignmentsPayload(payload: Record<string, unknown> | null): boolean {
  return (
    !!payload &&
    payload.error === 'event_locked' &&
    payload.message === 'event_assignments_read_only_after_active'
  );
}

function isRecalledOnlyWhenActivePayload(payload: Record<string, unknown> | null): boolean {
  return (
    !!payload &&
    payload.error === 'event_locked' &&
    payload.message === 'recalled_only_when_event_active'
  );
}

function isRaceLockedAssignmentsPayload(payload: Record<string, unknown> | null): boolean {
  return !!payload && payload.error === 'race_locked';
}

function isRecalledOnlyWhenRaceActivePayload(payload: Record<string, unknown> | null): boolean {
  return (
    !!payload &&
    payload.error === 'race_locked' &&
    payload.message === 'recalled_only_when_race_active'
  );
}

function isRecalledOnlyWhenRaceDistributionOrLaterPayload(payload: Record<string, unknown> | null): boolean {
  return (
    !!payload &&
    payload.error === 'race_locked' &&
    payload.message === 'recalled_only_when_race_distribution_or_later'
  );
}

function isDeviceAlreadyAssignedPayload(payload: Record<string, unknown> | null): boolean {
  return !!payload && payload.error === 'device_already_assigned';
}

function isDeviceAssignmentCasConflictPayload(payload: Record<string, unknown> | null): boolean {
  return !!payload && payload.error === 'device_assignment_conflict';
}

/** True when the server rejected an optimistic / CAS write (assignments changed concurrently). */
export function isDeviceAssignmentCasConflict(response: {
  status?: number;
  data?: unknown;
  error?: string | null;
} | null | undefined): boolean {
  const payload =
    response?.data && typeof response.data === 'object'
      ? (response.data as Record<string, unknown>)
      : null;
  if (isDeviceAssignmentCasConflictPayload(payload)) return true;
  const pre = typeof response?.error === 'string' ? response.error : '';
  return pre.includes('device_assignment_conflict');
}

function friendlyDeviceAssignmentConflictMessage(
  payload: Record<string, unknown> | null,
  status?: number,
): string {
  const message = typeof payload?.message === 'string' ? payload.message.trim() : '';
  if (message) return message;
  const statusNote = status ? ` (HTTP ${status})` : '';
  return `Assignments changed while saving; refresh and try again.${statusNote}`;
}

function friendlyEventLockedAssignmentsMessage(status?: number): string {
  const statusNote = status ? ` (HTTP ${status})` : '';
  return `Cannot change device or operator assignments while the event is active, completed, or deleted.${statusNote}`;
}

export function formatApiResponseError(
  response: {
    status?: number;
    data?: unknown;
    error?: string | null;
  } | null | undefined,
  fallback = 'Request failed',
): string {
  const preformattedError = typeof response?.error === 'string' ? response.error.trim() : '';
  const payload =
    response?.data && typeof response.data === 'object'
      ? (response.data as Record<string, unknown>)
      : null;

  // Prefer human copy even when main process also set `error` to `HTTP … | event_locked | …`.
  if (isEventLockedAssignmentsPayload(payload)) {
    return friendlyEventLockedAssignmentsMessage(response?.status);
  }
  if (isRecalledOnlyWhenActivePayload(payload)) {
    return 'Retrieved status can only be changed while the event is active.';
  }
  if (isRecalledOnlyWhenRaceDistributionOrLaterPayload(payload)) {
    return 'Retrieved status can only be changed while this race is in Distribution, Racking, Active, or Reporting.';
  }
  if (isRecalledOnlyWhenRaceActivePayload(payload)) {
    return 'Retrieved update is blocked by an older API policy. Deploy the latest event API and retry.';
  }
  if (isRaceLockedAssignmentsPayload(payload)) {
    return 'This race is active, completed, or deleted — assignments and configuration cannot be changed.';
  }
  if (isDeviceAssignmentCasConflictPayload(payload)) {
    return friendlyDeviceAssignmentConflictMessage(payload, response?.status);
  }
  if (isDeviceAlreadyAssignedPayload(payload)) {
    const message = typeof payload?.message === 'string' ? payload.message.trim() : '';
    return message || 'Device is already assigned elsewhere; release it before reassigning.';
  }
  if (
    preformattedError.includes('event_locked') &&
    preformattedError.includes('event_assignments_read_only_after_active')
  ) {
    return friendlyEventLockedAssignmentsMessage(response?.status);
  }
  if (
    preformattedError.includes('event_locked') &&
    preformattedError.includes('recalled_only_when_event_active')
  ) {
    return 'Retrieved status can only be changed while the event is active.';
  }
  if (
    preformattedError.includes('race_locked') &&
    preformattedError.includes('recalled_only_when_race_distribution_or_later')
  ) {
    return 'Retrieved status can only be changed while this race is in Distribution, Racking, Active, or Reporting.';
  }
  if (
    preformattedError.includes('race_locked') &&
    preformattedError.includes('recalled_only_when_race_active')
  ) {
    return 'Retrieved update is blocked by an older API policy. Deploy the latest event API and retry.';
  }
  if (preformattedError.includes('device_assignment_conflict')) {
    const afterPipe = preformattedError.replace(/^HTTP \d+:\s*device_assignment_conflict\s*\|\s*/i, '').trim();
    if (afterPipe && !afterPipe.toLowerCase().startsWith('http ')) {
      return afterPipe;
    }
    return friendlyDeviceAssignmentConflictMessage(payload, response?.status);
  }
  if (preformattedError.includes('device_already_assigned')) {
    return preformattedError.replace(/^HTTP \d+:\s*device_already_assigned\s*\|\s*/i, '');
  }

  if (preformattedError && (preformattedError.startsWith('HTTP ') || preformattedError.includes(' | '))) {
    return preformattedError;
  }

  const parts: string[] = [];
  const seen = new Set<string>();

  pushUniqueMessage(parts, seen, payload?.error);
  pushUniqueMessage(parts, seen, payload?.message);
  pushUniqueMessage(parts, seen, payload?.detail);
  pushUniqueMessage(parts, seen, preformattedError);

  const details = payload?.details;
  if (typeof details === 'string') {
    pushUniqueMessage(parts, seen, details);
  } else if (Array.isArray(details)) {
    for (const item of details) {
      if (typeof item === 'string') {
        pushUniqueMessage(parts, seen, item);
        continue;
      }
      if (item && typeof item === 'object') {
        pushUniqueMessage(parts, seen, JSON.stringify(item));
      }
    }
  }

  if (parts.length === 0) {
    return response?.status ? `${fallback} (HTTP ${response.status})` : fallback;
  }

  const statusPrefix = response?.status ? `HTTP ${response.status}: ` : '';
  return `${statusPrefix}${parts.join(' | ')}`;
}

export function toMs(ts?: number): number | null {
  if (!ts || ts <= 0) return null;
  if (ts > 1e12) return ts;
  return ts * 1000;
}

export function formatOperatorLabel(op: EventOperatorRow): string {
  const v =
    op.preferred_username ||
    op.email ||
    op.userEmail ||
    op.username ||
    op.userId;
  return String(v || '').trim() || op.userId;
}

export function formatUserDisplayNameFromApiUser(u: Record<string, unknown>): string {
  const preferredUsername =
    typeof u.preferred_username === 'string' ? u.preferred_username.trim() : '';
  if (preferredUsername) return preferredUsername;

  const username = typeof u.username === 'string' ? u.username.trim() : '';
  if (username) return username;

  const email =
    (typeof u.email === 'string' && u.email.trim()) ||
    (typeof u.userEmail === 'string' && u.userEmail.trim());
  if (email) return email;

  const id =
    (typeof u.userId === 'string' && u.userId.trim()) ||
    (typeof u.sub === 'string' && u.sub.trim());
  return id || '-';
}

export function buildGlobalUserIdToDisplayName(users: unknown[]): Record<string, string> {
  const out: Record<string, string> = {};
  if (!Array.isArray(users)) return out;

  for (const raw of users) {
    if (!raw || typeof raw !== 'object') continue;
    const u = raw as Record<string, unknown>;
    const label = formatUserDisplayNameFromApiUser(u);
    const uid = typeof u.userId === 'string' ? u.userId.trim() : '';
    const sub = typeof u.sub === 'string' ? u.sub.trim() : '';
    if (uid) out[uid] = label;
    if (sub) out[sub] = label;
    if (uid && sub && uid !== sub) {
      out[uid] = label;
      out[sub] = label;
    }
  }

  return out;
}

export function toUnixSeconds(value: string): number | undefined {
  if (!value) return undefined;
  const ts = new Date(value).getTime();
  if (Number.isNaN(ts) || ts <= 0) return undefined;
  return Math.floor(ts / 1000);
}

export function formatUserOptionLabel(user: EventUserOption): string {
  const preferred = String(user.preferred_username ?? '').trim();
  if (preferred) return preferred;

  const email = String(user.email ?? '').trim();
  const username = String(user.username ?? '').trim();
  if (email && username) return `${email} (${username})`;
  if (email) return email;
  if (username) return username;
  return user.userId;
}

export function groupAssignedDevicesByRace(
  devices: EventDeviceRow[],
  races: EventRaceSummaryRow[],
): DevicesGroupedByRace[] {
  const displayName = (raceId: string): string => {
    const row = races.find((r) => r.raceId === raceId);
    const n = row?.name != null ? String(row.name).trim() : '';
    return n || raceId;
  };

  const statusForRaceId = (raceId: string): string | undefined => {
    const row = races.find((r) => r.raceId === raceId);
    const s = row?.status != null ? String(row.status).trim() : '';
    return s || undefined;
  };

  const buckets = new Map<string, EventDeviceRow[]>();
  for (const d of devices) {
    const rid = String(d.raceId ?? '').trim();
    const key = rid || '__none__';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(d);
  }

  const out: DevicesGroupedByRace[] = [];
  const used = new Set<string>();

  for (const r of races) {
    const id = r.raceId;
    if (!id || used.has(id)) continue;
    const list = buckets.get(id);
    if (!list?.length) continue;
    used.add(id);
    out.push({
      raceKey: id,
      heading: `${displayName(id)} (${id})`,
      raceStatus: statusForRaceId(id),
      devices: list,
    });
  }

  const remaining = Array.from(buckets.keys()).filter((k) => !used.has(k)).sort();
  for (const key of remaining) {
    const list = buckets.get(key);
    if (!list?.length) continue;
    if (key === '__none__') {
      out.push({
        raceKey: '',
        heading: 'Devices without a race ID',
        devices: list,
      });
    } else {
      out.push({
        raceKey: key,
        heading: `${displayName(key)} (${key})`,
        raceStatus: statusForRaceId(key),
        devices: list,
      });
    }
  }

  return out;
}

export type RaceStatusFlowOptions = {
  /** Combined-mode races run collection + racking scans concurrently in DISTRIBUTION and skip RACKING. */
  combinedMode?: boolean;
};

/** True when the race row was created with combined collection + racking mode. */
export function isCombinedModeRace(race?: { combinedMode?: unknown } | null): boolean {
  const raw = race?.combinedMode;
  return raw === true || Number(raw ?? 0) === 1 || String(raw ?? '').trim().toLowerCase() === 'true';
}

export function getNextRaceStatus(status?: string | null, options?: RaceStatusFlowOptions): string | null {
  const current = normalizeRaceStatus(status ?? 'DRAFT');
  if (current === 'DELETED') return null;
  if (options?.combinedMode && current === 'DISTRIBUTION') return 'ACTIVE';
  const idx = RACE_STATUS_FLOW.indexOf(current as (typeof RACE_STATUS_FLOW)[number]);
  if (idx < 0 || idx >= RACE_STATUS_FLOW.length - 1) return null;
  return RACE_STATUS_FLOW[idx + 1];
}

/** Approved backward transitions (one step only). */
const RACE_STATUS_BACKWARD: ReadonlyArray<readonly [string, string]> = [
  ['PACKAGING', 'DRAFT'],
  ['DISTRIBUTION', 'PACKAGING'],
  ['RACKING', 'DISTRIBUTION'],
  ['ACTIVE', 'RACKING'],
  ['REPORTING', 'ACTIVE'],
  ['COMPLETE', 'REPORTING'],
];

export function getPrevRaceStatus(status?: string | null, options?: RaceStatusFlowOptions): string | null {
  const current = normalizeRaceStatus(status ?? 'DRAFT');
  if (options?.combinedMode && current === 'ACTIVE') return 'DISTRIBUTION';
  for (const [from, to] of RACE_STATUS_BACKWARD) {
    if (from === current) return to;
  }
  return null;
}

export function isAllowedRaceStatusTransition(
  currentStatus?: string | null,
  targetStatus?: string | null,
  options?: RaceStatusFlowOptions,
): boolean {
  const current = normalizeRaceStatus(currentStatus ?? '');
  const target = normalizeRaceStatus(targetStatus ?? '');
  if (!current || !target || current === 'DELETED' || target === 'DELETED') return false;
  if (options?.combinedMode && (current === 'RACKING' || target === 'RACKING')) return false;
  if (getNextRaceStatus(current, options) === target) return true;
  if (getPrevRaceStatus(current, options) === target) return true;
  return !options?.combinedMode && RACE_STATUS_BACKWARD.some(([from, to]) => from === current && to === target);
}

export function formatMoveToStatusLabel(
  direction: 'forward' | 'backward',
  targetStatus: string,
): string {
  const status = normalizeRaceStatus(targetStatus);
  return direction === 'forward'
    ? `Move forward to ${status}`
    : `Move backward to ${status}`;
}

/** Device/race configuration locks follow the race lifecycle (not the event row). */
export function isRaceAssignmentsLocked(status?: string | null): boolean {
  const current = String(status ?? '').trim().toUpperCase();
  return (
    current === 'DISTRIBUTION' ||
    current === 'RACKING' ||
    current === 'ACTIVE' ||
    current === 'REPORTING' ||
    current === 'COMPLETE' ||
    current === 'DELETED'
  );
}

export function isRaceRecallEditable(raceStatus?: string | null): boolean {
  const s = String(raceStatus ?? '').trim().toUpperCase();
  return s === 'ACTIVE' || s === 'REPORTING';
}

/** COMPLETE / DELETED races use history (and optional CSV fallback in the UI when history is empty). */
export function isRaceTerminalForDeviceHistory(status?: string | null): boolean {
  const s = String(status ?? '').trim().toUpperCase();
  return s === 'COMPLETE' || s === 'DELETED';
}

/** Event shell is read-only only when the event is soft-deleted. */
export function isEventDeletedOnly(status?: string | null): boolean {
  return String(status ?? '').trim().toUpperCase() === 'DELETED';
}

export function getEventStatusBadgeClass(status?: string | null): string {
  const current = normalizeRaceStatus(status ?? 'UNKNOWN') || 'UNKNOWN';
  if (current === 'OPEN') {
    return 'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-700/60 dark:bg-sky-950/40 dark:text-sky-200';
  }
  if (current === 'DRAFT') {
    return 'border-gray-300 bg-gray-100 text-gray-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';
  }
  if (current === 'PACKAGING') {
    return 'border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200';
  }
  if (current === 'DISTRIBUTION') {
    return 'border-sky-300 bg-sky-100 text-sky-800 dark:border-sky-700/60 dark:bg-sky-950/40 dark:text-sky-200';
  }
  if (current === 'RACKING') {
    return 'border-cyan-300 bg-cyan-100 text-cyan-800 dark:border-cyan-700/60 dark:bg-cyan-950/40 dark:text-cyan-200';
  }
  if (current === 'ACTIVE') {
    return 'border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-200';
  }
  if (current === 'REPORTING') {
    return 'border-indigo-300 bg-indigo-100 text-indigo-800 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-200';
  }
  if (current === 'COMPLETE') {
    return 'border-violet-300 bg-violet-100 text-violet-800 dark:border-violet-700/60 dark:bg-violet-950/40 dark:text-violet-200';
  }
  if (current === 'DELETED') {
    return 'border-red-300 bg-red-50 text-red-800 dark:border-red-700/60 dark:bg-red-950/40 dark:text-red-200';
  }
  return 'border-gray-300 bg-white text-gray-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';
}

export function getRaceStatusBadgeClass(status?: string | null): string {
  return getEventStatusBadgeClass(status);
}

/**
 * Event API `_pagination` caps each `listEventDevices` request at 200 and returns `nextToken` when more
 * rows exist. Callers that need the full set must follow tokens until exhausted.
 */
export const EVENT_LIST_DEVICES_PAGE_SIZE = 200;

export async function fetchAllEventDevicesRaw(
  eventId: string,
  options?: { raceId?: string },
): Promise<{
  ok: boolean;
  devices: unknown[];
}> {
  if (typeof window === 'undefined' || !window.eventsAPI?.listEventDevices) {
    return { ok: false, devices: [] };
  }
  const all: unknown[] = [];
  let nextToken: string | undefined;
  for (; ;) {
    const res = await window.eventsAPI.listEventDevices(eventId, {
      limit: EVENT_LIST_DEVICES_PAGE_SIZE,
      ...(nextToken ? { nextToken } : {}),
      ...(options?.raceId ? { raceId: options.raceId } : {}),
    });
    if (!res?.ok) {
      if (all.length === 0) {
        return { ok: false, devices: [] };
      }
      break;
    }
    const data = res.data as { devices?: unknown[]; nextToken?: string | null } | undefined;
    const chunk = Array.isArray(data?.devices) ? data.devices : [];
    all.push(...chunk);
    const t = data?.nextToken;
    if (t == null || String(t).trim() === '') break;
    nextToken = String(t);
  }
  return { ok: true, devices: all };
}
